import type { WritableComputedRef } from 'vue';
import type { AnyItem, CompositePrimaryKey, RelationOperationToOneInUpdate, ResolvedMonospaceSingleRelationField } from '#layers/data-model/public';
import type { FormBlockContext } from '#layers/forms/types/form-block';
import { toValue } from 'vue';
import { getItemPrimaryKey, getRelationPrimitiveFields, isConnectToOne, isCreateToOne, isDisconnectToOne, isSameKey, isUpdateToOne, nestedToOneConnect } from '#layers/data-model/public';
import {
	createdItemKey,
	fromReferencedItemKey,
	isCreatedItem,
	originalItemKey,
	verifyItemKeys,
} from '#layers/forms/composables/relation-field-state-primitives';

export function createRelationFieldStateToOneInUpdateHandler(field: ResolvedMonospaceSingleRelationField, fieldValue: WritableComputedRef<RelationOperationToOneInUpdate | undefined, RelationOperationToOneInUpdate | undefined>, formBlockContext: FormBlockContext | null) {
	function handleCreate(payload: AnyItem) {
		fieldValue.value ??= [];
		fieldValue.value = fieldValue.value.filter((op) => !isConnectToOne(op) && !isDisconnectToOne(op));
		fieldValue.value.push({ _create: { data: { ...payload, [createdItemKey]: crypto.randomUUID() } } });
		const { fields: pkFields } = getRelationPrimitiveFields(field);
		const formState = formBlockContext?.state.value;

		if (!formState) return;

		for (const field of pkFields) {
			formState[field.apiName] = null;
		}
	}

	function handleConnect(key: CompositePrimaryKey) {
		fieldValue.value = nestedToOneConnect({ key, mode: 'update' });

		const primitiveValues = fromReferencedItemKey(key, field);
		const formState = formBlockContext?.state.value;

		if (!primitiveValues || !formState) return;

		for (const [fieldKey, value] of Object.entries(primitiveValues)) {
			formState[fieldKey] = value;
		}
	}

	function handleDisconnect(key: CompositePrimaryKey) {
		fieldValue.value ??= [];
		const isNewlyConnectedItem = fieldValue.value.some((op) => {
			return isConnectToOne(op) && isSameKey(op._connect.key, key);
		});
		const isNewlyCreatedItem = fieldValue.value.some((op) => {
			return isCreateToOne(op);
		});

		if (isNewlyConnectedItem) {
			fieldValue.value = fieldValue.value.filter((op) => !isConnectToOne(op));
		}

		if (isNewlyCreatedItem) {
			fieldValue.value = fieldValue.value.filter((op) => !isCreateToOne(op));
		}
		else {
			fieldValue.value = [{ _disconnect: {} }];
		}

		// make sure the primitive fields on this item are set to `null`
		const primitiveValues = fromReferencedItemKey(key, field);
		const formState = formBlockContext?.state.value;

		if (!primitiveValues || !formState) return;

		for (const fieldKey of Object.keys(primitiveValues)) {
			formState[fieldKey] = null;
		}
	}

	function handleUpdate(originalKey: CompositePrimaryKey | null, payload: AnyItem) {
		const oppositeCollection = toValue(field).oppositeCollection!;
		fieldValue.value ??= [];
		originalKey ??= getItemPrimaryKey(payload, oppositeCollection);
		verifyItemKeys(originalKey, payload, oppositeCollection);

		if (isCreatedItem(payload)) {
			fieldValue.value = fieldValue.value.map((op) => {
				if (isCreateToOne(op)) {
					return { _create: { data: { ...payload, [originalItemKey]: originalKey } } };
				}
				else {
					return op;
				}
			});
		}
		else if (fieldValue.value.some((op) => isUpdateToOne(op))) {
			fieldValue.value = fieldValue.value.map((op) => {
				if (isUpdateToOne(op)) {
					return {
						_update: {
							data: { ...payload, [originalItemKey]: originalKey },
							filter: getItemPrimaryKey(payload, oppositeCollection),
						},
					};
				}
				else {
					return op;
				}
			});
		}
		else {
			fieldValue.value.push({ _update: {
				data: { ...payload, [originalItemKey]: originalKey },
				filter: getItemPrimaryKey(payload, oppositeCollection),
			} });
		}
	}

	return {
		handleConnect,
		handleDisconnect,
		handleCreate,
		handleUpdate,
	};
}
